import { useEffect, useRef } from "react";

const IGNORED_KEYS = new Set([
  "Alt",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "Backspace",
  "CapsLock",
  "Control",
  "Escape",
  "Meta",
  "Shift",
  "Tab",
]);

function isEditableTarget(target) {
  if (!target) return false;
  const tagName = target.tagName?.toLowerCase();
  if (tagName === "input") {
    return target.dataset?.barcodeInput !== "true";
  }
  return tagName === "textarea" || tagName === "select" || target.isContentEditable;
}

export function useBarcodeScanner({
  disabled = false,
  maxInterKeyDelay = 60,
  minLength = 8,
  onScan,
  timeoutMs = 120,
} = {}) {
  const bufferRef = useRef("");
  const firstKeyAtRef = useRef(0);
  const lastKeyAtRef = useRef(0);
  const onScanRef = useRef(onScan);
  const timerRef = useRef(null);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    if (disabled) {
      bufferRef.current = "";
      return undefined;
    }

    const clearTimer = () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const reset = () => {
      clearTimer();
      bufferRef.current = "";
      firstKeyAtRef.current = 0;
      lastKeyAtRef.current = 0;
    };

    const commit = () => {
      const code = bufferRef.current.trim();
      const duration = Math.max(1, lastKeyAtRef.current - firstKeyAtRef.current);
      const maxBurstDuration = Math.max(240, code.length * maxInterKeyDelay);
      reset();

      if (code.length >= minLength && duration <= maxBurstDuration) {
        onScanRef.current?.(code);
      }
    };

    const scheduleCommit = () => {
      clearTimer();
      timerRef.current = window.setTimeout(commit, timeoutMs);
    };

    const handleKeyDown = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (isEditableTarget(event.target)) return;

      if (event.key === "Enter") {
        if (bufferRef.current) {
          event.preventDefault();
          commit();
        }
        return;
      }

      if (IGNORED_KEYS.has(event.key) || event.key.length !== 1) return;

      const now = Date.now();
      const lastKeyAt = lastKeyAtRef.current;
      const isNewBurst = !bufferRef.current || now - lastKeyAt > maxInterKeyDelay;

      if (isNewBurst) {
        bufferRef.current = "";
        firstKeyAtRef.current = now;
      }

      bufferRef.current += event.key;
      lastKeyAtRef.current = now;
      scheduleCommit();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      clearTimer();
    };
  }, [disabled, maxInterKeyDelay, minLength, timeoutMs]);
}
